import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Check, Copy } from 'lucide-react';
import { useSuperAdminApiTokens } from '@/contexts/SuperAdminApiTokensContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const GlobalApiTokensDialog: React.FC = () => {
  const { isOpen, selectedBrandId, closeApiTokensDialog } = useSuperAdminApiTokens();
  const [apiTokens, setApiTokens] = useState<any>(null);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [tokenSearchQuery, setTokenSearchQuery] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && selectedBrandId) {
      fetchApiTokens();
    }
  }, [isOpen, selectedBrandId]);

  const fetchApiTokens = async () => {
    if (!selectedBrandId) return;
    
    setIsLoadingTokens(true);
    try {
      const { data, error } = await supabase
        .from('api_tokens')
        .select('*')
        .eq('brand_id', selectedBrandId)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      setApiTokens(data);
    } catch (error) {
      console.error('Error fetching API tokens:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch API tokens',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingTokens(false);
    }
  };

  const handleUpdateApiToken = async (field: string, value: string) => {
    if (!selectedBrandId) return;
    
    try {
      const { error } = await supabase
        .from('api_tokens')
        .update({ [field]: value })
        .eq('brand_id', selectedBrandId);
      
      if (error) throw error;
      
      setApiTokens((prev: any) => ({ ...prev, [field]: value }));
      toast({
        title: 'Success',
        description: 'API token updated',
      });
    } catch (error) {
      console.error('Error updating API token:', error);
      toast({
        title: 'Error',
        description: 'Failed to update API token',
        variant: 'destructive',
      });
    }
  };

  const handleCopyToken = async (field: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      toast({
        title: 'Copied!',
        description: 'Token copied to clipboard',
      });
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy token',
        variant: 'destructive',
      });
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeApiTokensDialog();
      setApiTokens(null);
      setTokenSearchQuery('');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>API Tokens for Brand</DialogTitle>
          <CardDescription>
            View and edit API tokens. Changes are saved automatically.
          </CardDescription>
        </DialogHeader>
        {isLoadingTokens ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse">Loading tokens...</div>
          </div>
        ) : apiTokens ? (
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search API tokens..."
                value={tokenSearchQuery}
                onChange={(e) => setTokenSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(apiTokens)
                .filter(([key]) => {
                  if (['id', 'brand_id', 'user_id', 'created_at', 'updated_at', 'prompts_per_combination'].includes(key)) {
                    return false;
                  }
                  if (key.endsWith('_verified') || key.endsWith('_verified_at') || key.endsWith('_model')) {
                    return false;
                  }
                  if (tokenSearchQuery) {
                    return key.toLowerCase().includes(tokenSearchQuery.toLowerCase());
                  }
                  return true;
                })
                .map(([key, value]) => (
                  <div key={key} className="space-y-2">
                    <label className="text-sm font-medium capitalize">
                      {key.replace(/_/g, ' ')}
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={(value as string) || ''}
                        onChange={(e) => handleUpdateApiToken(key, e.target.value)}
                        placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                        className="font-mono text-xs"
                      />
                      {value && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyToken(key, value as string)}
                          className="shrink-0"
                        >
                          {copiedField === key ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No API tokens configured for this brand.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
